import { useEffect, useState } from "react";
import {
  Heart,
  Thermometer,
  Activity,
  Droplets,
  Settings,
  Bell,
  User,
  Calendar,
  LogOut,
  LayoutDashboard,
  ClipboardList,
  AlertCircle,
  Zap,
  ArrowUpRight,
  Menu
} from "lucide-react";

import "./PatientDashboard.css";
import VitalsCard from "../../components/VitalsCard";
import LiveChart from "../../components/LiveChart";
import ECGChart from "../../components/ECGChart";
import DeviceInfoCard from "../../components/DeviceInfoCard";
import RecentActivity from "../../components/RecentActivity";
import PatientSidebar from "../../components/PatientSidebar";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/config";
import { ref, onValue, set, push } from "firebase/database";

export default function PatientDashboard() {
  const { currentUser, userData } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [vitals, setVitals] = useState({
    heartRate: 0,
    spo2: 0,
    temperature: 0,
    bloodPressure: "0/0",
  });
  const [nextAppointment, setNextAppointment] = useState(null);

  const [activities] = useState([
    { title: "Vitals recorded", time: "2 minutes ago", type: "normal" },
    { title: "Heart rate spike detected", time: "15 minutes ago", type: "warning" },
    { title: "Medication reminder", time: "1 hour ago", type: "normal" },
    { title: "Regular checkup completed", time: "3 hours ago", type: "normal" },
  ]);

  useEffect(() => {
    if (!currentUser) return;

    // 1. Listen to real-time vitals from Firebase
    const vitalsRef = ref(db, `vitals/${currentUser.uid}`);
    const historyRef = ref(db, `vitals_history/${currentUser.uid}`);
    
    const unsubscribeVitals = onValue(vitalsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) setVitals(data);
    });

    // 2. Synchronize Clinical Session Registry
    const appointmentsRef = ref(db, "appointments");
    const unsubscribeAppts = onValue(appointmentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const now = new Date();
        const myAppts = Object.values(data)
          .filter(appt => appt.patientUid === currentUser.uid)
          .map(appt => ({
            ...appt,
            fullDate: new Date(`${appt.date}T${appt.time || "00:00"}`)
          }))
          .filter(appt => appt.fullDate >= now)
          .sort((a, b) => a.fullDate - b.fullDate);
          
        setNextAppointment(myAppts.length > 0 ? myAppts[0] : null);
      } else {
        setNextAppointment(null);
      }
    });

    // 3. Simulation: Push random vitals to Firebase (Simulating IoT Sensor)
    const interval = setInterval(() => {
      const newVitals = {
        heartRate: 72 + Math.floor(Math.random() * 20),
        spo2: 96 + Math.floor(Math.random() * 4),
        temperature: 36.5 + Math.random() * 1.2,
        bloodPressure: `${110 + Math.floor(Math.random() * 20)}/${70 + Math.floor(Math.random() * 15)}`,
        lastSync: new Date().toISOString()
      };

      set(vitalsRef, newVitals);
      
      // Also push a record to history (we only keep some for demo)
      push(historyRef, {
        hr: newVitals.heartRate,
        spo2: newVitals.spo2,
        temp: newVitals.temperature,
        timestamp: new Date().toISOString()
      });
    }, 5000);

    return () => {
      unsubscribeVitals();
      unsubscribeAppts();
      clearInterval(interval);
    };
  }, [currentUser]);

  const hasAlert = vitals.heartRate > 90 || vitals.temperature > 37.5;

  return (
    <div className="dashboard-shell">
      <div className={`sidebar-overlay ${isSidebarOpen ? "active" : ""}`} onClick={() => setIsSidebarOpen(false)}></div>
      <div className={`sidebar-container ${isSidebarOpen ? "active" : ""}`}>
        <PatientSidebar toggleSidebar={() => setIsSidebarOpen(false)} />
      </div>

      {/* Main Content Area */}
      <main className="dashboard-content">
        <header className="dashboard-topbar">
          <div className="topbar-left">
            <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
              <Menu size={24} strokeWidth={2.5} />
            </button>
            <div className="title-icon">
              <Activity size={24} className="icon-blue" />
            </div>
            <div className="title-text">
              <h1>My Health Dashboard</h1>
              <p>Real-time health monitoring</p>
            </div>
          </div>

          <div className="topbar-right">
            <div className="patient-id-card">
              <div className="patient-avatar">
                <User size={18} />
              </div>
              <div className="patient-meta">
                <span className="meta-label">Patient ID</span>
                <span className="meta-value">{userData?.customId || "LOADING..."}</span>
              </div>
            </div>
            
            <button className="topbar-action-btn glass-card">
              <Settings size={18} />
            </button>
            <button className="topbar-action-btn glass-card alert">
              <Bell size={18} />
              <span className="notification-dot"></span>
            </button>
          </div>
        </header>

        <div className="scrollable-content">
          {/* Top Row: Device & Appointment */}
          <div className="dashboard-row multi-col">
            <DeviceInfoCard
              deviceId="DEV-HM-2024-001"
              deviceName="HealthMonitor Pro X1"
              batteryLevel={78}
              isConnected={true}
              lastSync="2 seconds ago"
            />

            <div className={`appointment-card glass-card ${!nextAppointment ? "empty-state" : ""}`}>
              <div className="appointment-header">
                <Calendar size={18} className="icon-blue" />
                <span>{nextAppointment ? "Next Appointment" : "Clinical Schedule"}</span>
              </div>
              <div className="appointment-main">
                <h2 className="appointment-date">
                  {nextAppointment 
                    ? new Date(nextAppointment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : "NO_PENDING_SESSIONS"}
                </h2>
                <span className="appointment-doctor">
                  {nextAppointment 
                    ? `${nextAppointment.time} – ${nextAppointment.doctor}` 
                    : "Registry synchronized. No upcoming nodes detected."}
                </span>
              </div>
              {nextAppointment && (
                <div className="appointment-tags">
                  <span className="tag-badge">{nextAppointment.specialty || "General Consultation"}</span>
                  <ArrowUpRight size={16} className="tag-arrow" />
                </div>
              )}
            </div>
          </div>

          {/* Conditional Alert Banner */}
          {hasAlert && (
            <div className="alert-banner-red">
              <div className="alert-content">
                <AlertCircle size={22} />
                <div className="alert-text">
                  <strong>Alert: Abnormal Vitals Detected</strong>
                  <span>One or more vital signs are outside normal range. Please consult your healthcare provider.</span>
                </div>
              </div>
            </div>
          )}

          {/* Vitals Grid */}
          <section className="dashboard-section">
            <div className="vitals-grid-four">
              <VitalsCard 
                title="Heart Rate" 
                value={vitals.heartRate} 
                unit="bpm" 
                icon={Heart} 
                status={vitals.heartRate > 85 ? "warning" : "normal"}
              />
              <VitalsCard 
                title="SpO2" 
                value={vitals.spo2} 
                unit="%" 
                icon={Activity} 
                status={vitals.spo2 < 96 ? "warning" : "normal"}
              />
              <VitalsCard 
                title="Temperature" 
                value={vitals.temperature.toFixed(1)} 
                unit="°C" 
                icon={Thermometer} 
                status={vitals.temperature > 37.2 ? "warning" : "normal"}
              />
              <VitalsCard 
                title="Blood Pressure" 
                value={vitals.bloodPressure} 
                unit="mmHg" 
                icon={Droplets} 
              />
            </div>
          </section>

          {/* Charts Section */}
          <section className="dashboard-section">
            <div className="charts-container">
              <div className="chart-wrapper">
                <LiveChart title="Heart Rate Trend" height={220} />
              </div>
              <div className="chart-wrapper">
                <LiveChart title="Blood Oxygen (SpO2)" height={220} />
              </div>
            </div>
          </section>

          {/* ECG and Recent Activity */}
          <section className="dashboard-section bottom-row">
            <div className="ecg-section">
              <div className="ecg-header">
                <h3>Electrocardiogram (ECG)</h3>
                <div className="live-indicator">
                  <span className="pulse-dot"></span>
                  <span>Live</span>
                </div>
              </div>
              <div className="ecg-chart-box glass-card">
                <ECGChart />
              </div>
            </div>

            <RecentActivity activities={activities} />
          </section>
        </div>
      </main>
    </div>
  );
}
